// GroupDetailIsland.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, UserMinus, AlertCircleIcon, Copy, Mail, Link as LinkIcon, MoreHorizontal, Pencil, Trash2, ArrowBigLeft, ArrowLeft, ChevronLeft } from 'lucide-react';
import './GroupDetailIsland.css';
import MeetingDetailView from './ui/MeetingDetailView';
import {
  getGroup,
  getGroupMembers,
  getGroupMeetings,
  updateGroup,
  deleteGroup,
  createGroupInviteLink,
  inviteGroupMemberEmail,
  updateGroupMemberRole
  ,
  removeGroupMember,
  deleteAssignedPracticeMeeting,

} from '../../../../../api/groupApi';
import Dropdown from '../../../../../components/Dropdown/Dropdown';
import { useToast } from '../../../../../components/Toaster/use-toast';
import { getOrganizationMembers } from '../../../../../api/organizationApi';
import Input from '../../../../../components/Input/Input';
import PracticeSetup from '../../../../PracticeSetup/PracticeSetup';
import { formatNice } from '../../../../../utils/formatNice';
import { formatMeetingDate } from '../../../../../utils/formatTime';
import Pagination from '../../../../../components/Pagination/Pagination';
import TableScroll from '../../../../../components/TableScroll/TableScroll';


function TabButton({ active, children, onClick }) {
  return (
    <button className={`gd-tab ${active ? 'active' : ''}`} onClick={onClick}>
      {children}
    </button>
  );
}

function Modal({ open, title, onClose, children }) {
  if (!open) return null;
  return (
    <div className="gd-modal-backdrop" onMouseDown={onClose}>
      <div className="gd-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="gd-modal-header">
          <div className="gd-modal-title">{title}</div>
          <button className="gd-icon-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="gd-modal-body">{children}</div>
      </div>
    </div>
  );
}

const TYPE_OPTIONS = [
  { label: 'Classroom', value: 'classroom' },
  { label: 'Team', value: 'team' },
  { label: 'Program', value: 'program' },
  { label: 'Cohort', value: 'cohort' },
  { label: 'Department', value: 'department' },
];
const GROUP_ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin', help: 'Can manage members, roles, and invites for this group.' },
  { value: 'member', label: 'Member', help: 'Can participate in meetings but cannot manage group settings.' },
];


function formatType(t) {
  if (!t) return '—';
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function nameFromMember(m) {
  const full = `${m.firstName || ''} ${m.lastName || ''}`.trim();
  return full || '—';
}

function memberState(m) {
  return m.inviteAccepted ? 'Active' : 'Invited';
}

function RowActionsMenu({ onEditPermissions, onRemove, canEdit, canRemove }) {
  console.log(canEdit)
  return (
    <div
      className="gd-row-actions-menu"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        className="gd-menu-item"
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onEditPermissions();
        }}
        disabled={!canEdit}
        title={!canEdit ? "You can’t edit your own permissions" : "Edit permissions"}
      >
        Edit permissions
      </button>

      <button
        className="gd-menu-item danger"
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        disabled={!canRemove}
        title={!canRemove ? "Not allowed" : "Remove from group"}
      >
        Remove user
      </button>
    </div>
  );
}



function MeetingsRowActionsMenu({ onOpen, onDelete }) {
  return (
    <div
      className="gd-row-actions-menu"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="gd-menu-item"
        onClick={(e) => {
          e.stopPropagation();
          onOpen();
        }}
      >
        Open
      </button>

      <button
        type="button"
        className="gd-menu-item danger"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
      >
        Delete conversation
      </button>
    </div>
  );
}


export default function GroupDetailIsland({ organizationId, groupId, currentUserId }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const view = searchParams.get('view') || 'members'; // members | meetings | settings
  const meetingId = searchParams.get('meetingId');
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [memberQuery, setMemberQuery] = useState('');
  const [orgMembers, setOrgMembers] = useState([]);
  const [loadingOrgMembers, setLoadingOrgMembers] = useState(false);
  const [inviteOpenList, setInviteOpenList] = useState(false);
  // API state
  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [meetings, setMeetings] = useState([]);

  const [loadingGroup, setLoadingGroup] = useState(true);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loadingMeetings, setLoadingMeetings] = useState(false);

  const [errMsg, setErrMsg] = useState('');
  const { toast } = useToast();
  // Invite modal state (still UI-only for now)
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('Member');
  const [inviteError, setInviteError] = useState('');
  const [sending, setSending] = useState(false);
  // Settings form state
  const [editName, setEditName] = useState('');
  const [editType, setEditType] = useState('team');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [inviteLinkRole, setInviteLinkRole] = useState('member'); // group role
  const [inviteLinkExpires, setInviteLinkExpires] = useState('7'); // days as string
  const [generatedInviteUrl, setGeneratedInviteUrl] = useState('');
  const [generatingLink, setGeneratingLink] = useState(false);
  const [copyOk, setCopyOk] = useState(false);

  const [showGroupMenu, setShowGroupMenu] = useState(false);
  const [showEditGroupModal, setShowEditGroupModal] = useState(false);
  const [editErr, setEditErr] = useState('');

  const [pendingRole, setPendingRole] = useState({}); // { [userId]: 'admin'|'member' }
  const [savingRole, setSavingRole] = useState({});  // { [userId]: true/false }
  const [rowRoleError, setRowRoleError] = useState({}); // { [userId]: 'msg' }
  const [removingMember, setRemovingMember] = useState({}); // { [userId]: true/false }
  const [rowRemoveError, setRowRemoveError] = useState({});
  const [showSetup, setShowSetup] = useState(false);
  const [editingMeetingAssignees, setEditingMeetingAssignees] = useState([]);
  const [meetPage, setMeetPage] = useState(1);
  const [meetPages, setMeetPages] = useState(1);
  const [memberPage, setMemberPage] = useState(1);
  const [memberPages, setMemberPages] = useState(1);
  const PAGE_SIZE = 10;
  const MEMBERS_PAGE_SIZE = 10;

  const [rowMenuOpenFor, setRowMenuOpenFor] = useState(null); // userId | null
  const [meetingMenuOpenFor, setMeetingMenuOpenFor] = useState(null); // meetingId | null

  // --- permissions modal
  const [permOpen, setPermOpen] = useState(false);
  const [permMember, setPermMember] = useState(null); // member object
  const [permRole, setPermRole] = useState("member");
  const [permSaving, setPermSaving] = useState(false);
  const [permError, setPermError] = useState("");

  useEffect(() => {
    let alive = true;

    async function loadGroup() {
      setLoadingGroup(true);
      setErrMsg('');
      try {
        const data = await getGroup(organizationId, groupId);
        if (!alive) return;
        setGroup(data);
        setEditName(data?.name || '');
        setEditType((data?.type || 'team').toLowerCase());
      } catch (e) {
        if (!alive) return;
        setErrMsg(e?.response?.data?.error || e?.message || 'Failed to load group');
      } finally {
        if (!alive) return;
        setLoadingGroup(false);
      }
    }

    if (organizationId && groupId) loadGroup();
    return () => {
      alive = false;
    };
  }, [organizationId, groupId]);

  useEffect(() => {
    let alive = true;

    async function loadMembers() {
      setLoadingMembers(true);
      setErrMsg('');
      try {
        const data = await getGroupMembers(organizationId, groupId, {
          page: memberPage,
          pageSize: MEMBERS_PAGE_SIZE,
        });

        if (!alive) return;

        const items = Array.isArray(data?.items) ? data.items : [];
        setMembers(items);
        setMemberPages(Number(data?.pages) || 1);

        // keep UI in sync if backend clamped page
        if (data?.page && data.page !== memberPage) setMemberPage(data.page);

        // init pending role state for visible page only
        const init = {};
        items.forEach((m) => {
          init[m.id] = m.role; // 'admin' | 'member'
        });
        setPendingRole(init);

      } catch (e) {
        if (!alive) return;
        setErrMsg(e?.response?.data?.error || e?.message || 'Failed to load members');
      } finally {
        if (!alive) return;
        setLoadingMembers(false);
      }
    }

    if (view === 'members' && organizationId && groupId) loadMembers();
    return () => { alive = false; };
  }, [view, organizationId, groupId, memberPage]);


  useEffect(() => {
    let alive = true;

    async function loadMeetings() {
      setLoadingMeetings(true);
      setErrMsg('');
      try {
        const data = await getGroupMeetings(organizationId, groupId, {
          page: meetPage,
          pageSize: PAGE_SIZE,
        });

        if (!alive) return;

        setMeetings(Array.isArray(data?.items) ? data.items : []);
        setMeetPages(Number(data?.pages) || 1);

        // keep UI in sync if backend clamped page
        if (data?.page && data.page !== meetPage) setMeetPage(data.page);
      } catch (e) {
        if (!alive) return;
        setErrMsg(e?.response?.data?.error || e?.message || 'Failed to load meetings');
      } finally {
        if (!alive) return;
        setLoadingMeetings(false);
      }
    }

    if (view === 'meetings' && !meetingId && organizationId && groupId) loadMeetings();
    return () => { alive = false; };
  }, [view, meetingId, organizationId, groupId, meetPage]);


  useEffect(() => {
    function onDocClick(e) {
      if (!e.target.closest(".gd-title-actions")) setShowGroupMenu(false);
      if (!e.target.closest(".gd-row-actions")) setRowMenuOpenFor(null);
      if (!e.target.closest(".gd-meeting-row-actions")) setMeetingMenuOpenFor(null);
    }
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);
  


  const setView = (next) => {
    const sp = new URLSearchParams(searchParams);
    sp.set('tab', 'groups');
    sp.set('groupId', String(groupId));
    sp.set('view', next);
    sp.delete('meetingId');
    setSearchParams(sp);
  };

  const goBack = () => {
    navigate(`/org/${organizationId}/management?tab=groups`);
  };

  const openMeeting = (mid) => {
    const sp = new URLSearchParams(searchParams);
    sp.set('tab', 'groups');
    sp.set('groupId', String(groupId));
    sp.set('view', 'meetings');
    sp.set('meetingId', String(mid));
    setSearchParams(sp);
  };

  const filteredMembers = useMemo(() => {
    const q = memberQuery.trim().toLowerCase();
    if (!q) return members;

    return members.filter((m) => {
      const nm = nameFromMember(m).toLowerCase();
      const em = (m.email || '').toLowerCase();
      const rl = (m.role || '').toLowerCase();
      const st = memberState(m).toLowerCase();
      return nm.includes(q) || em.includes(q) || rl.includes(q) || st.includes(q);
    });
  }, [memberQuery, members]);

  const groupMemberIds = useMemo(() => new Set(members.map(m => Number(m.id))), [members]);

  function roleLabel(role) {
    return role === "admin" ? "Admin" : "Member";
  }
  const filteredOrgMembers = useMemo(() => {
    const q = inviteEmail.trim().toLowerCase();
    if (!q) return [];

    return orgMembers
      .filter(u => !groupMemberIds.has(Number(u.id))) // exclude existing group members
      .filter(u => {
        const full = `${u.first_name || ''} ${u.last_name || ''}`.trim().toLowerCase();
        const em = (u.email || '').toLowerCase();
        return full.includes(q) || em.includes(q);
      })
      .slice(0, 8); // limit dropdown
  }, [inviteEmail, orgMembers, groupMemberIds]);

  const openInviteModal = async () => {
    setInviteEmail('');
    setInviteRole('Member');
    setInviteError('');
    setShowInviteModal(true);
    setInviteOpenList(true);

    setInviteLinkRole('member');
    setInviteLinkExpires('7');
    setGeneratedInviteUrl('');
    setCopyOk(false);

    if (!organizationId) return;

    setLoadingOrgMembers(true);
    try {
      const res = await getOrganizationMembers(organizationId);

      // ✅ support BOTH response shapes
      const items = Array.isArray(res) ? res : (res?.items || []);
  
      setOrgMembers(items)
    } catch (e) {
      setInviteError(e?.response?.data?.error || e?.message || 'Failed to load org members');
    } finally {
      setLoadingOrgMembers(false);
    }
  };

  const openPermModal = (m) => {
    console.log("openPermModal fired", m?.id);
    setPermError("");
    setPermMember(m);
    setPermRole(m?.role || "member");
    setPermOpen(true);
    setRowMenuOpenFor(null);
  };

  const closePermModal = () => {
    setPermOpen(false);
    setPermMember(null);
    setPermError("");
  };
  const handleGenerateInviteLink = async () => {
    setInviteError('');
    setCopyOk(false);

    if (!organizationId || !groupId) {
      setInviteError('Missing organization or group.');
      return;
    }

    setGeneratingLink(true);
    try {
      // backend should accept: { groupRole, expiresInDays }
      const expiresInDays = inviteLinkExpires === '0' ? null : Number(inviteLinkExpires);

      const res = await createGroupInviteLink(organizationId, groupId, {
        groupRole: inviteLinkRole, // "member" | "admin" etc
        ...(expiresInDays ? { expiresInDays } : {}),
      });

      // Expecting backend returns { inviteUrl } OR { inviteLink }
      const url = res?.inviteUrl || res?.inviteLink || res?.url;
      if (!url) throw new Error('Invite link was created but no URL was returned.');

      setGeneratedInviteUrl(url);
    } catch (e) {
      setInviteError(e?.response?.data?.error || e?.message || 'Failed to generate invite link');
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCopyInviteLink = async () => {
    if (!generatedInviteUrl) return;
    try {
      await navigator.clipboard.writeText(generatedInviteUrl);
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 1200);
    } catch {
      setInviteError('Could not copy. Please copy the link manually.');
    }
  };

  const handleEmailInviteLink = () => {
    if (!generatedInviteUrl) return;

    const subject = encodeURIComponent(`Join ${group?.name || 'my group'} on Telome`);
    const body = encodeURIComponent(
      `Hey!\n\nUse this link to join the group "${group?.name || ''}":\n${generatedInviteUrl}\n\n`
    );

    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };


  const handleInvite = async () => {
    setInviteError('');

    const email = inviteEmail.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      setInviteError('Please enter a valid email address');
      return;
    }

    if (!organizationId || !groupId) {
      setInviteError('Missing organization or group.');
      return;
    }

    // Map your UI roles -> backend roles
    // Backend expects: 'admin' | 'member'
    const groupRole =
      String(inviteRole || '').toLowerCase() === 'admin' ? 'admin' : 'member';

    setSending(true);
    try {
      await inviteGroupMemberEmail(organizationId, groupId, {
        email,
        groupRole,
      });

      // optional: clear email after success
      setInviteEmail('');

      // refresh members so UI updates immediately
      const updated = await getGroupMembers(organizationId, groupId, {
        page: memberPage,
        pageSize: MEMBERS_PAGE_SIZE,
      });
      setMembers(Array.isArray(updated?.items) ? updated.items : []);
      setMemberPages(Number(updated?.pages) || 1);

      // optional: close modal on success
      // setShowInviteModal(false);
    } catch (e) {
      setInviteError(e?.response?.data?.error || e?.message || 'Failed to send invite');
    } finally {
      setSending(false);
    }
  };



  const handleSaveGroup = async () => {
    if (!editName.trim()) return;

    setSaving(true);
    setErrMsg('');
    try {
      const res = await updateGroup(organizationId, groupId, {
        name: editName.trim(),
        type: editType,
      });

      // controller returns { message, group }
      const updated = res?.group || res;
      setGroup((prev) => ({ ...(prev || {}), ...(updated || {}) }));
    } catch (e) {
      setErrMsg(e?.response?.data?.error || e?.message || 'Failed to update group');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGroup = async () => {
    setDeleting(true);
    setErrMsg('');
    try {
      await deleteGroup(organizationId, groupId);
      goBack();
    } catch (e) {
      setErrMsg(e?.response?.data?.error || e?.message || 'Failed to delete group');
    } finally {
      setDeleting(false);
    }
  };
  const savePermRole = async () => {
    if (!permMember) return;

    const userId = permMember.id;
    const existing = permMember.role;
    const next = permRole;

    if (!next || next === existing) return;

    setPermSaving(true);
    setPermError("");

    try {
      const resp = await updateGroupMemberRole(organizationId, groupId, userId, next);
      const updatedRole = resp?.member?.role ?? next;

      // update list
      setMembers((prev) =>
        prev.map((m) =>
          Number(m.id) === Number(userId) ? { ...m, role: updatedRole } : m
        )
      );

      // update modal member snapshot so disable logic stays accurate
      setPermMember((prev) => (prev ? { ...prev, role: updatedRole } : prev));
      setPermOpen(false);
      toast({ title: "Permissions updated", description: "Role updated successfully." });
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        "Failed to update role";
      setPermError(msg);
    } finally {
      setPermSaving(false);
    }
  };


  // owner id guard (depends on how getGroup returns it)
  const ownerId = useMemo(() => {
    const raw =
      group?.createdBy ??
      group?.created_by ??
      group?.created_by_id ??
      group?.ownerId ??
      null;

    return raw != null ? Number(raw) : null;
  }, [group]);

  const canRemoveMember = (m) => {
    // UI guardrails (backend still enforces final rules)
    if (!currentUserId) return false;

    // Don’t show remove for the owner
    if (ownerId && Number(m.id) === ownerId) return false;

    // Optional: don't let you remove yourself (usually good UX)
    if (Number(m.id) === Number(currentUserId)) return false;

    // If you want to restrict UI to group admins (even though backend requires org-admin),
    // keep this; otherwise set `return true` and just let backend 403.
    return canManageRoles; // group admin
  };

  const handleRemoveMember = async (m) => {
    if (!organizationId || !groupId) return;

    const displayName = nameFromMember(m);
    const ok = window.confirm(`Remove ${displayName} from this group?`);
    if (!ok) return;

    setRowRemoveError((prev) => ({ ...prev, [m.id]: '' }));
    setRemovingMember((prev) => ({ ...prev, [m.id]: true }));

    try {
      await removeGroupMember(organizationId, groupId, m.id);

      // remove from UI
      setMembers((prev) => prev.filter((x) => Number(x.id) !== Number(m.id)));

      // cleanup role state
      setPendingRole((prev) => {
        const next = { ...prev };
        delete next[m.id];
        return next;
      });

      toast({
        title: 'Member removed',
        description: `${displayName} was removed from "${group?.name || 'this group'}".`,
      });
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        'Failed to remove member';

      setRowRemoveError((prev) => ({ ...prev, [m.id]: msg }));

      toast({
        title: 'Could not remove member',
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setRemovingMember((prev) => ({ ...prev, [m.id]: false }));
    }
  };

  const isSelf = (userId) => currentUserId && Number(currentUserId) === Number(userId);

  const myGroupRole = useMemo(() => {
    const meRow = members.find((m) => Number(m.id) === Number(currentUserId));
    return meRow?.role || null;
  }, [members, currentUserId]);

  const canManageRoles = myGroupRole === 'admin';


  if (loadingGroup) {
    return (
      <div className="gd-island">
        <div className="gd-topbar">
          <button className="gd-back" onClick={goBack}><ChevronLeft size={24} /> Back to Groups</button>
        </div>
        <div className="gd-header">
          <h2 className="gd-title">Loading group…</h2>
        </div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="gd-island">
        <div className="gd-topbar">
          <button className="gd-back" onClick={goBack}><ChevronLeft size={24} /> Back to Groups</button>
        </div>
        <div className="gd-header">
          <h2 className="gd-title">Group not found</h2>
          {!!errMsg && <div className="gd-muted">{errMsg}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="gd-island">
      {/* Header */}
      <div className="gd-topbar">
        <button className="gd-back" onClick={goBack}><ChevronLeft size={24} /> Back to Groups</button>
      </div>

      {!!errMsg && <div className="gd-muted" style={{ marginBottom: 12 }}>{errMsg}</div>}

      <div className="gd-header">
        <div className="gd-header-row">
          <div>
            <div className="gd-title-row">
              <h2 className="gd-title">{group.name}</h2>

              <div className="gd-title-actions">
                <button
                  className="gd-kebab"
                  onClick={() => setShowGroupMenu((v) => !v)}
                  aria-label="Group actions"
                >
                  <MoreHorizontal size={24} />
                </button>

                {showGroupMenu && (
                  <div className="gd-menu">
                    <button
                      type="button"
                      className="gd-menu-item"
                      onClick={() => {
                        setShowGroupMenu(false);
                        setEditErr('');
                        setShowEditGroupModal(true);
                      }}
                    >
                      <Pencil size={16} />
                      Edit group details
                    </button>

                    <button
                      className="gd-menu-item danger"
                      onClick={async () => {
                        setShowGroupMenu(false);
                        // confirm + call existing handler
                        const ok = window.confirm(
                          `Delete "${group.name}"? This cannot be undone.`
                        );
                        if (ok) await handleDeleteGroup();
                      }}
                    >
                      <Trash2 size={16} />
                      Delete group
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="gd-subline">
              <span className="gd-pill">{formatType(group.type)}</span>
              <span className="gd-dot">•</span>
              <span className="gd-muted">{members.length || group.memberCount || 0} members</span>
              <span className="gd-dot">•</span>
              <span className="gd-muted">Owner: {group.ownerName || '—'}</span>
            </div>
          </div>

        </div>


      </div>

      {/* Tabs */}
      <div className="gd-tabs">
        <TabButton active={view === 'members'} onClick={() => setView('members')}>Members</TabButton>
        <TabButton active={view === 'meetings'} onClick={() => setView('meetings')}>Meetings</TabButton>
      </div>

      {/* Content */}
      <div className="gd-content">
        {view === 'members' && (
          <>
            <div className="gd-toolbar">
              <div className="gd-search">
                <Input
                  value={memberQuery}
                  onChange={(e) => setMemberQuery(e.target.value)}
                  placeholder="Search members…" />

              </div>

              <div className="gd-toolbar-actions">
                <button className="gd-secondary" onClick={openInviteModal}>
                  <Plus size={16} />
                  Invite Members
                </button>
              </div>
            </div>

            {loadingMembers ? (
              <div className="gd-muted">Loading members…</div>
            ) : (
              <>
              <TableScroll>
                 <div className="gd-table">
                  <div className="gd-row gd-head gd-row-roles">
                    <div>Name</div>
                    <div>Email</div>
                    <div>Role</div>
                    <div>Status</div>
                    <div style={{ width: 60 }}></div>
                  </div>


                  {filteredMembers.map((m) => {
                    const status = memberState(m);
                    const isMenuOpen = rowMenuOpenFor === m.id;

                    return (
                      <div className="gd-row gd-row-roles" key={m.id}>
                        <div className="gd-strong">{nameFromMember(m)}</div>
                        <div className="gd-muted">{m.email}</div>

                        {/* Readonly role label */}
                        <div>
                          <span className={`gd-role-pill ${m.role === "admin" ? "admin" : "member"}`}>
                            {roleLabel(m.role)}
                          </span>
                        </div>

                        <div>
                          <span className={`gd-state ${status === "Active" ? "active" : "invited"}`}>
                            {status}
                          </span>
                        </div>

                        {/* More (⋯) */}
                        <div className="gd-right gd-row-actions">
                          <button
                            className="gd-kebab"
                            type="button"
                            aria-label="Member actions"
                            onClick={(e) => {
                              e.stopPropagation();
                              setRowMenuOpenFor((prev) => (prev === m.id ? null : m.id));
                            }}
                          >
                            <MoreHorizontal size={24} />
                          </button>

                          {isMenuOpen && (
                            <RowActionsMenu
                              canEdit={canManageRoles && !(isSelf(m.id) && permRole !== "admin")}
                              canRemove={canRemoveMember(m)}
                              onEditPermissions={() => openPermModal(m)}
                              onRemove={() => handleRemoveMember(m)}
                            />
                          )}

                          {rowRemoveError[m.id] && (
                            <div className="gd-row-inline-error">{rowRemoveError[m.id]}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                </div>
              </TableScroll>
               
                <div className="userprofile-pagination">
                  <Pagination page={memberPage} pages={memberPages} onChange={setMemberPage} />
                </div>

              </>


            )}

         

          </>
        )}

        {view === 'meetings' && (
          <>
            {meetingId ? (
              editingMeeting ? (
                // Show PracticeSetup in edit mode
                <PracticeSetup
                  meeting={editingMeeting}           // ← pass the meeting to pre-fill
                  currentAssignments={editingMeetingAssignees}
                  organizationId={organizationId}
                  groupId={groupId}
                  onBack={() => {
                    setEditingMeeting(null);
                    const sp = new URLSearchParams(searchParams);
                    sp.delete('meetingId');
                    setSearchParams(sp);
                  }}
                  onAssigned={() => {
                    setEditingMeeting(null);
                    // Refetch meetings to see updated data
                    setMeetings([]); // trigger reload
                  }}
                />
              ) : (
                (() => {
                  const selectedMeeting = meetings.find(m => m.id === Number(meetingId));
                  if (!selectedMeeting) {
                    return (
                      <div className="gd-meeting-detail">
                        <button className="gd-back" onClick={() => {
                          const sp = new URLSearchParams(searchParams);
                          sp.delete('meetingId');
                          setSearchParams(sp);
                        }}>
                          ← Back to Meetings
                        </button>
                        <div className="gd-muted">Meeting not found.</div>
                      </div>
                    );
                  }

                  return (

                    <MeetingDetailView
                      meeting={selectedMeeting}
                      organizationId={organizationId}
                      groupId={groupId}
                      onBack={() => {
                        const sp = new URLSearchParams(searchParams);
                        sp.delete('meetingId');
                        setSearchParams(sp);
                      }}
                      onEdit={
                        // async () => {
                        // // Fetch assignees before switching to edit mode
                        // try {
                        //   const assigneesData = await getMeetingAssignees(organizationId, groupId, selectedMeeting.id);
                        //   setEditingMeetingAssignees(
                        //     (assigneesData || []).map(a => ({
                        //       userId: a.userId,
                        //       scheduledFor: a.scheduledFor || null,
                        //     }))
                        //   );
                        // } catch (err) {
                        //   console.error("Failed to load assignees for edit:", err);
                        //   setEditingMeetingAssignees([]);
                        //   toast({
                        //     title: "Could not load assignees",
                        //     description: "You can still edit the meeting details.",
                        //     variant: "destructive"
                        //   });
                        // }
                        // setEditingMeeting(selectedMeeting);}
                        null
                      }
                    />
                  );
                })()
              )
            ) : (
              <>



                {!showSetup ? (
                  <div>
                    <div className="gd-toolbar">
                      <button className="gd-primary" onClick={() => setShowSetup(true)}>
                        <Plus size={24} /> Assign
                      </button>
                    </div>

                    {loadingMeetings ? (
                      <div className="gd-muted" style={{ padding: '60px 0', textAlign: 'center' }}>
                        Loading meetings…
                      </div>
                    ) : meetings.length === 0 ? (
                      <div className="gd-muted" style={{ padding: '80px 0', textAlign: 'center' }}>
                        No conversations yet.<br />
                        Click <strong>Assign</strong> to assign your first one.
                      </div>
                    ) : (
                      <TableScroll>
  <div className="gd-table gd-table-meetings">
                        {/* Header */}
                        <div className="gd-row gd-head gd-row-meetings">
                          <div>Conversation Type</div>
                          <div>Duration</div>
                          <div>Created</div>
                          <div>Actions</div>
                        </div>

                        {/* Rows */}
                        {meetings.map((meeting) => {
                          const displayType =
                            meeting.meetingType === 'custom' && meeting.customMeeting
                              ? meeting.customMeeting
                              : formatNice(meeting.meetingType || 'Practice Meeting');

                          const createdAgo = formatMeetingDate(meeting.createdAt);
                          const isMeetingMenuOpen = meetingMenuOpenFor === meeting.id;
                          return (
                            <div key={meeting.id} className="gd-row gd-row-meetings"  onClick={() => openMeeting(meeting.id)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                openMeeting(meeting.id);
                              }
                            }} >
                              {/* Column 1: Meeting Type */}
                              <div className="gd-strong">{displayType}</div>

                              {/* Column 2: Duration */}
                              <div>{meeting.duration} min</div>

                              {/* Column 3: Created At (relative time) */}
                              <div className="gd-muted">{createdAgo}</div>

                              {/* Column 4: Actions */}
       

<div className="gd-right gd-meeting-row-actions" style={{ position: "relative", display: "flex", justifyContent: "flex-end" }}   onMouseDown={(e) => e.stopPropagation()}
  onClick={(e) => e.stopPropagation()}>
  <button
    className="gd-kebab"
    type="button"
    aria-label="Meeting actions"
    onClick={(e) => {
      e.stopPropagation();
      setMeetingMenuOpenFor((prev) => (prev === meeting.id ? null : meeting.id));
    }}
  >
    <MoreHorizontal size={24} />
  </button>

  {isMeetingMenuOpen && (
    <MeetingsRowActionsMenu
      onOpen={() => {
        setMeetingMenuOpenFor(null);
        openMeeting(meeting.id);
      }}
      onDelete={async () => {
        setMeetingMenuOpenFor(null);

        const displayType =
          meeting.meetingType === "custom" && meeting.customMeeting
            ? meeting.customMeeting
            : formatNice(meeting.meetingType || "Practice Meeting");

        const confirmed = window.confirm(
          `Permanently delete "${displayType}"?\n\nThis will remove the meeting and all assignments. This cannot be undone.`
        );

        if (!confirmed) return;

        try {
          await deleteAssignedPracticeMeeting(organizationId, groupId, meeting.id);

          setMeetings((prev) => prev.filter((m) => m.id !== meeting.id));

          toast({
            title: "Meeting deleted",
            description: `"${displayType}" has been permanently removed.`,
          });
        } catch (err) {
          console.error("Failed to delete meeting:", err);
          toast({
            title: "Could not delete meeting",
            description: err?.response?.data?.error || "Something went wrong",
            variant: "destructive",
          });
        }
      }}
    />
  )}
</div>

                            </div>
                          );
                        })}
                      </div>

                      </TableScroll>
                    
                    )}
                  </div>
                ) : (
                  <PracticeSetup
                    onBack={() => setShowSetup(false)}
                    onAssigned={() => setShowSetup(false)}
                    organizationId={organizationId}
                    groupId={groupId}
                  />
                )}
                <div className="userprofile-pagination">
                  <Pagination page={meetPage} pages={meetPages} onChange={setMeetPage} />
                </div>
              </>
            )}
          </>
        )}

      </div>

         {/* Invite modal (still UI-only until group-scoped invite exists) */}
         <Modal open={showInviteModal} title="Invite Members" onClose={() => setShowInviteModal(false)}>
              <div className="gd-import-title">
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <Mail size={18} />
                  Invite by email
                </span>
              </div>

              <div className="gd-muted" style={{ marginBottom: 12 }}>
                Invite an existing organization member to this group. (They must already be in the org.)
              </div>

              <div className="gd-email-invite-row">
                <div className="gd-email-invite-field" style={{ position: 'relative' }}>
                  <label className="gd-label">Email</label>

                  <input
                    className="gd-field"
                    type="email"
                    placeholder="Search org users by name or email…"
                    value={inviteEmail}
                    onChange={(e) => {
                      setInviteEmail(e.target.value);
                      setInviteOpenList(true);
                    }}
                    onFocus={() => setInviteOpenList(true)}
                    onBlur={() => {
                      // slight delay so click can register
                      setTimeout(() => setInviteOpenList(false), 120);
                    }}
                  />

                  {inviteOpenList && inviteEmail.trim() && (
                    <div className="gd-invite-suggest">
                      {loadingOrgMembers ? (
                        <div className="gd-invite-suggest-row muted">Loading…</div>
                      ) : filteredOrgMembers.length ? (
                        filteredOrgMembers.map((u) => (
                          <button
                            key={u.id}
                            type="button"
                            className="gd-invite-suggest-row"
                            onMouseDown={(e) => e.preventDefault()} // prevents blur before click
                            onClick={() => {
                              setInviteEmail(u.email);
                              setInviteOpenList(false);
                            }}
                          >
                            <div className="gd-invite-suggest-ident">
                              <div className="gd-invite-avatar">
                                {(u.first_name || u.email || '?').slice(0, 1).toUpperCase()}
                              </div>
                              <div>
                                <div className="gd-invite-name">
                                  {`${u.first_name || ''} ${u.last_name || ''}`.trim() || '—'}
                                </div>
                                <div className="gd-invite-email">{u.email}</div>
                              </div>
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="gd-invite-suggest-row muted">No matches</div>
                      )}
                    </div>
                  )}
                </div>


                <div>
                  <label className="gd-label">Group role</label>
                  <select
                    className="gd-field"
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                  >
                    <option value="Member">Member</option>
                    <option value="Admin">Admin</option>
                  </select>
                </div>

                <div >

                  <button className="gd-primary" onClick={handleInvite} disabled={sending}>
                    {sending ? 'Sending…' : 'Send Invite'}
                  </button>

                </div>
              </div>



              {inviteError && (
                <div className="gd-error">
                  <AlertCircleIcon size={16} />
                  <span>{inviteError}</span>
                </div>
              )}

              <div className="gd-invite-divider">
                <span>or share a link</span>
              </div>
              <div className="gd-import-box">
                <div className="gd-import-title">
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <LinkIcon size={18} />
                    Shareable invite link
                  </span>
                </div>

                <div className="gd-muted" style={{ marginBottom: 12 }}>
                  Generate a link that org members can use to join this group instantly.
                </div>

                <div className="gd-invite-link-row">
                  <div className="gd-invite-link-field">
                    <label className="gd-label">Group role</label>
                    <select
                      className="gd-field"
                      value={inviteLinkRole}
                      onChange={(e) => setInviteLinkRole(e.target.value)}
                    >
                      <option value="member">Member</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>

                  <div className="gd-invite-link-field">
                    <label className="gd-label">Expires</label>
                    <select
                      className="gd-field"
                      value={inviteLinkExpires}
                      onChange={(e) => setInviteLinkExpires(e.target.value)}
                    >
                      <option value="7">7 days</option>
                      <option value="30">30 days</option>
                      <option value="0">Never</option>
                    </select>
                  </div>

                  <button
                    className="gd-primary gd-generate-link-btn"
                    onClick={handleGenerateInviteLink}
                    disabled={generatingLink}
                    type="button"
                  >
                    <LinkIcon size={16} />
                    <span>{generatingLink ? 'Generating…' : 'Generate Link'}</span>
                  </button>
                </div>

                {generatedInviteUrl && (
                  <div style={{ marginTop: 12 }}>
                    <label className="gd-label">Invite URL</label>

                    <div className="gd-invite-url-row">
                      <input className="gd-field gd-invite-url-input" value={generatedInviteUrl} readOnly />

                      <button className="gd-secondary gd-invite-url-btn" onClick={handleCopyInviteLink} type="button">
                        <Copy size={16} />
                        <span>{copyOk ? 'Copied!' : 'Copy'}</span>
                      </button>

                      <button className="gd-secondary gd-invite-url-btn" onClick={handleEmailInviteLink} type="button">
                        <Mail size={16} />
                        <span>Email</span>
                      </button>
                    </div>
                  </div>
                )}

              </div>




            </Modal>

            <Modal
              open={showEditGroupModal}
              title="Edit group details"
              onClose={() => setShowEditGroupModal(false)}
            >
              <div className="gd-modal-subtext">
                Update the group name or type.
              </div>

              <label className="gd-label">Group Name</label>
              <Input

                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Group name"
              />

              <label className="gd-label">Group Type</label>
              <Dropdown
  options={TYPE_OPTIONS}
  value={editType || null}
  onChange={(v) => setEditType(v)}
  placeholder="Select type…"
  ariaLabel="Group type"

/>


              {!!editErr && <div className="gd-error" style={{ marginTop: 10 }}>{editErr}</div>}

              <div className="gd-modal-actions">
                <button className="gd-secondary" onClick={() => setShowEditGroupModal(false)}>
                  Cancel
                </button>

                <button
                  className="gd-primary"
                  disabled={saving}
                  onClick={async () => {
                    setEditErr('');
                    try {
                      await handleSaveGroup(); // uses your updateGroup(orgId, groupId, payload)
                      setShowEditGroupModal(false);
                    } catch (e) {
                      setEditErr(e?.response?.data?.error || e?.message || 'Failed to update group');
                    }
                  }}
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </Modal>
            <Modal
              open={permOpen}
              title={`Edit permissions${permMember ? ` — ${nameFromMember(permMember)}` : ""}`}
              onClose={closePermModal}
            >
              <div className="gd-muted" style={{ marginBottom: 10 }}>
                Choose a group role for this member.
              </div>

              <Dropdown
                options={GROUP_ROLE_OPTIONS}
                value={permRole}
                onChange={(role) => setPermRole(role)}
                disabled={!canManageRoles || permSaving || (permMember && isSelf(permMember.id))}
                placeholder="Select role…"
                ariaLabel="Group role"
              />

              {permMember && isSelf(permMember.id) && (
                <div className="gd-row-inline-help" style={{ marginTop: 8 }}>
                  You can’t change your own role here.
                </div>
              )}

              {permError && (
                <div className="gd-error" style={{ marginTop: 10 }}>
                  <AlertCircleIcon size={16} />
                  <span>{permError}</span>
                </div>
              )}

              <div className="gd-modal-actions" style={{ marginTop: 14 }}>
                <button className="gd-secondary" onClick={closePermModal} type="button">
                  Cancel
                </button>

                <button
                  className="gd-primary"
                  onClick={savePermRole}
                  disabled={
                    permSaving ||
                    !canManageRoles ||
                    !permMember ||
                    permRole === (permMember?.role || "member")
                  }
                  type="button"
                >
                  {permSaving ? "Saving…" : "Save changes"}
                </button>
              </div>
            </Modal>
    </div>
  );
}
